import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Trash2, Database } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface DataSectionProps {
  userId: string;
}

export const DataSection = ({ userId }: DataSectionProps) => {
  const [exporting, setExporting] = useState(false);
  const [clearing, setClearing] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      // Fetch all user data
      const [promptsRes, savedRes, profileRes] = await Promise.all([
        supabase.from('prompts').select('*').eq('user_id', userId),
        supabase.from('saved_prompts').select('*').eq('user_id', userId),
        supabase.from('profiles').select('*').eq('id', userId).single(),
        supabase.from('profiles').select('*').eq('id', userId).single(),
      ]);

      const exportData = {
        exportedAt: new Date().toISOString(),
        profile: profileRes.data,
        prompts: promptsRes.data || [],
        savedPrompts: savedRes.data || [],
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `thepromptspace-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleClearActivity = async () => {
    setClearing(true);
    try {
      // Clear saved prompts (keeping prompts as they're content)
      await supabase.from('saved_prompts').delete().eq('user_id', userId);

      toast.success('Activity cleared successfully');
    } catch (error) {
      console.error('Error clearing activity:', error);
      toast.error('Failed to clear activity');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          Data & Privacy
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Export Data */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-0.5">
            <p className="font-medium">Export Your Data</p>
            <p className="text-sm text-muted-foreground">
              Download all your prompts, comments, and activity as JSON
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleExportData}
            disabled={exporting}
            className="w-full sm:w-auto"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export Data'}
          </Button>
        </div>

        {/* Clear Activity */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-border/50">
          <div className="space-y-0.5">
            <p className="font-medium">Clear Activity</p>
            <p className="text-sm text-muted-foreground">
              Remove your likes and saved prompts (keeps your posts)
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="w-full sm:w-auto">
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Activity
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all activity?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove all your likes and saved prompts. Your posts and comments will be kept. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleClearActivity}
                  disabled={clearing}
                >
                  {clearing ? 'Clearing...' : 'Clear Activity'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};
